/**
 * DealPeopleEditDialog - Modal para editar Responsável e Vendedor do negócio
 *
 * Versão: 1.0.0
 * RELEASE: 2026-08-09 v1.0.0 - Versão inicial. Espelha o padrão visual do
 *          DealInfoEditDialog e reaproveita UserSelector/SellerSelector
 *          (mesmos componentes usados no DealFormDialog).
 *
 * REGRA: A exibição do modal é controlada pelo componente pai, que só libera
 * o acesso quando o usuário tem permissão de edição do negócio (respeitando o
 * toggle de permissões por fase).
 */
import React, { useEffect, useState } from 'react';
import { CrmDeal } from '@/hooks/useCrmDeals';
import { UserSelector } from '@/components/crm/UserSelector';
import { SellerSelector } from '@/components/crm/SellerSelector';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// Props do componente
interface DealPeopleEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deal: CrmDeal;
  onSave: (data: {
    assigned_to: string | null;
    seller_id: string | null;
  }) => void | Promise<void>;
  isSaving?: boolean;
}

export function DealPeopleEditDialog({
  open,
  onOpenChange,
  deal,
  onSave,
  isSaving = false,
}: DealPeopleEditDialogProps) {
  // Estados locais isolados até o momento de salvar
  const [assignedTo, setAssignedTo] = useState<string | null>(deal.assigned_to ?? null);
  const [sellerId, setSellerId] = useState<string | null>(deal.seller_id ?? null);

  // Reset ao abrir
  useEffect(() => {
    if (open) {
      setAssignedTo(deal.assigned_to ?? null);
      setSellerId(deal.seller_id ?? null);
    }
  }, [open, deal]);

  // Só habilita salvar quando houver alteração real
  const hasChanges =
    (assignedTo || null) !== (deal.assigned_to || null) ||
    (sellerId || null) !== (deal.seller_id || null);

  const handleSave = () => {
    if (!hasChanges) return;
    onSave({ assigned_to: assignedTo, seller_id: sellerId });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Editar Pessoas</DialogTitle>
          <DialogDescription>
            Altere o responsável interno e o vendedor deste negócio.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Responsável interno */}
          <div className="space-y-2">
            <Label>Responsável</Label>
            <UserSelector
              value={assignedTo}
              onSelect={setAssignedTo}
              placeholder="Selecione..."
            />
          </div>

          {/* Vendedor responsável */}
          <div className="space-y-2">
            <Label>Vendedor</Label>
            <SellerSelector value={sellerId} onSelect={setSellerId} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !hasChanges}>
            {isSaving ? 'Salvando...' : 'Salvar Alterações'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
